import React, { useEffect, useState } from 'react';
import { analyticsApi, teacherApi, adminApi } from '../../api/client';
import Spinner from '../../components/common/Spinner';
import StatCard from '../../components/common/StatCard';
import { FaUsers, FaChartBar, FaExclamationTriangle, FaTrophy, FaFilePdf } from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';

const gradeColor = v => v >= 75 ? '#10b981' : v >= 50 ? '#f59e0b' : '#ef4444';
const gradeSymbol = v => v >= 80?'A':v >= 70?'B':v >= 60?'C':v >= 50?'D':v >= 40?'E':'F';
const PIE_COLORS = ['#10b981','#0d6efd','#f59e0b','#8b5cf6','#ef4444','#6b7280'];

export default function TeacherAnalytics() {
  const [data,    setData]    = useState(null);
  const [grades,  setGrades]  = useState([]);
  const [terms,   setTerms]   = useState([]);
  const [gradeId, setGradeId] = useState('');
  const [termId,  setTermId]  = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([teacherApi.getGrades(), adminApi.getTerms()])
      .then(([g,t]) => { setGrades(g.data.data); setTerms(t.data.data); });
  }, []);

  useEffect(() => {
    setLoading(true);
    analyticsApi.getTeacher({ grade_id: gradeId || undefined, term_id: termId || undefined })
      .then(r => setData(r.data.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [gradeId, termId]);

  const pieData = data ? Object.entries(data.grade_distribution || {}).filter(([,v])=>v>0).map(([k,v])=>({name:k,value:v})) : [];

  const openReportCard = (learnerId) => {
    const url = analyticsApi.getReportCard(learnerId, termId || 0);
    window.open(url, '_blank');
  };

  return (
    <div>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div><h2>Class Analytics</h2><p>Performance for your classes</p></div>
        <div style={{ display:'flex', gap:10 }}>
          <select className="sms-input" style={{width:180}} value={gradeId} onChange={e=>setGradeId(e.target.value)}>
            <option value="">All Grades</option>
            {grades.map(g=><option key={g.grade_id} value={g.grade_id}>Grade {g.grade_number}</option>)}
          </select>
          <select className="sms-input" style={{width:160}} value={termId} onChange={e=>setTermId(e.target.value)}>
            <option value="">All Terms</option>
            {terms.map(t=><option key={t.term_id} value={t.term_id}>{t.name} {t.year}</option>)}
          </select>
        </div>
      </div>

      {loading ? <Spinner /> : !data || data.summary?.total_learners === 0 ? (
        <div className="sms-card" style={{textAlign:'center',color:'#9ca3af',padding:'60px 0'}}>
          No performance data yet — add assessments and enter marks first
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:16, marginBottom:28 }}>
            <StatCard label="Total Learners" value={data.summary.total_learners} icon={FaUsers}               color="#0d6efd" />
            <StatCard label="Class Average"  value={`${data.summary.class_avg}%`} icon={FaChartBar}           color={gradeColor(data.summary.class_avg)} />
            <StatCard label="Passing"         value={data.summary.passing}         icon={FaTrophy}             color="#10b981" sub="≥ 50%" />
            <StatCard label="At Risk"          value={data.summary.at_risk}         icon={FaExclamationTriangle} color="#ef4444" sub="< 50%" />
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1.5fr 1fr', gap:24, marginBottom:24 }}>
            {/* Subject averages bar chart */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Subject Averages</h5>
              {data.subject_performance?.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.subject_performance} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis type="number" domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10}} />
                    <YAxis type="category" dataKey="subject_name" tick={{fontSize:10}} width={90} />
                    <Tooltip formatter={v=>[`${v}%`,'Avg']} />
                    <Bar dataKey="avg_pct" radius={[0,4,4,0]} fill="#0d6efd"
                      label={{position:'right',fontSize:10,formatter:v=>v?`${v}%`:''}} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:40}}>No subject data</p>}
            </div>

            {/* Grade distribution pie */}
            <div className="sms-card">
              <h5 style={{fontWeight:700,marginBottom:16}}>Grade Distribution</h5>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={85}
                      dataKey="value" label={({name,value})=>`${name}: ${value}`} labelLine={false}>
                      {pieData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p style={{color:'#9ca3af',textAlign:'center',paddingTop:60}}>No data</p>}
            </div>
          </div>

          {/* Assessment trend */}
          {data.assessment_trend?.length > 1 && (
            <div className="sms-card" style={{marginBottom:24}}>
              <h5 style={{fontWeight:700,marginBottom:16}}>Class Average per Assessment (Timeline)</h5>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={data.assessment_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="assessment_name" tick={{fontSize:9}} interval={0} angle={-20} textAnchor="end" height={40} />
                  <YAxis domain={[0,100]} tickFormatter={v=>`${v}%`} tick={{fontSize:10}} />
                  <Tooltip formatter={v=>[`${v}%`,'Class Avg']} />
                  <Line type="monotone" dataKey="class_avg" stroke="#0d6efd" strokeWidth={2} dot={{r:4}} name="Class Avg" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Learner performance table */}
          <div className="sms-card" style={{padding:0,overflow:'hidden'}}>
            <div style={{padding:'16px 20px',fontWeight:700,borderBottom:'1px solid #e5e7eb',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span>Learner Performance</span>
              <span style={{fontSize:'0.78rem',color:'#9ca3af'}}>{data.learner_performance?.length} learners</span>
            </div>
            <table className="sms-table">
              <thead><tr><th>#</th><th>Learner</th><th>Grade</th><th>Average</th><th>Symbol</th><th>Attendance</th><th>Report Card</th></tr></thead>
              <tbody>
                {data.learner_performance?.map((l, i) => {
                  const avg = parseFloat(l.avg_pct);
                  const col = gradeColor(avg);
                  const attPct = l.total_att_days > 0 ? Math.round((l.present_days/l.total_att_days)*100) : null;
                  return (
                    <tr key={l.user_id}>
                      <td style={{color:'#9ca3af',fontWeight:600}}>{i+1}</td>
                      <td style={{fontWeight:600}}>{l.name}</td>
                      <td>Gr {l.grade_number}</td>
                      <td><span style={{fontWeight:700,color:col}}>{avg}%</span></td>
                      <td><span style={{fontWeight:800,fontSize:'1rem',color:col}}>{gradeSymbol(avg)}</span></td>
                      <td>
                        {attPct !== null ? (
                          <span style={{color:attPct>=75?'#10b981':'#ef4444',fontWeight:600}}>{attPct}%</span>
                        ) : '—'}
                      </td>
                      <td>
                        <button onClick={() => openReportCard(l.user_id)}
                          className="sms-btn sms-btn-outline sms-btn-sm" style={{color:'#0d6efd'}}>
                          <FaFilePdf size={11}/> PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
